import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationService } from "@/services/notification.service";
import type { PaginatedResponse } from "@/types/payment";
import type {
  NotificationListParams,
  ServerNotification,
  UnreadNotificationCount,
} from "@/types/notification";

/**
 * Query keys for the current user's in-app notifications.
 */
export const notificationKeys = {
  all: ["notifications"] as const,
  lists: () => [...notificationKeys.all, "list"] as const,
  list: (params?: NotificationListParams) =>
    [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
};

/**
 * The current user's notifications, newest first. Callers refetch on their
 * own trigger (e.g. the bell panel opening) rather than relying solely on
 * `staleTime`, since a missed push should not wait out the full window.
 */
export function useNotificationsList(
  params?: NotificationListParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationService.getNotifications(params),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

/**
 * Unread notification count for the bell badge. Polled every 60s so the
 * badge stays current even when the panel is closed.
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => notificationService.getUnreadCount(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/**
 * Marks one notification as read.
 *
 * Optimistic: patches every cached notification list and decrements the
 * unread count immediately. The caller is expected to only invoke this for
 * a currently-unread notification (the bell checks `isRead` first), so the
 * count is decremented unconditionally here. Reconciled with the server on
 * settle regardless of outcome.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      queryClient.setQueriesData<PaginatedResponse<ServerNotification>>(
        { queryKey: notificationKeys.lists() },
        (current) =>
          current && {
            ...current,
            data: current.data.map((n) =>
              n.id === id ? { ...n, isRead: true } : n,
            ),
          },
      );

      queryClient.setQueryData<UnreadNotificationCount>(
        notificationKeys.unreadCount(),
        (current) =>
          current ? { count: Math.max(0, current.count - 1) } : current,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Marks every unread notification of the current user as read. Optimistic,
 * same reconciliation strategy as {@link useMarkNotificationRead}.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      queryClient.setQueriesData<PaginatedResponse<ServerNotification>>(
        { queryKey: notificationKeys.lists() },
        (current) =>
          current && {
            ...current,
            data: current.data.map((n) => ({ ...n, isRead: true })),
          },
      );

      queryClient.setQueryData<UnreadNotificationCount>(
        notificationKeys.unreadCount(),
        { count: 0 },
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
